import {
    Grid,
    InputAdornment,
    TableBody,
    TableCell,
    TableRow,
    Toolbar,
    Typography,
} from "@material-ui/core";
import { useEffect, useState } from "react";
import { AiOutlineSearch } from "react-icons/ai";
import { IoMdAdd } from "react-icons/io";
import Admin from "../../components/auth/Admin";
import { isAuth } from "../../actions/auth";
import Controls from "../../components/controls/Controls";
import Notify from "../../components/customHelpers/Notify";
import Popup from "../../components/customHelpers/Popup";
import NewsForm from "../../components/forms/NewsForm";
import Layout from "../../components/layout/Layout";
import News from "../../actions/News";
import useTable from "../../components/customHelpers/useTable";
import { MdOutlineDelete } from "react-icons/md";
import { FiEdit, FiEye } from "react-icons/fi";
import Loading from "../../components/customHelpers/Loading";
import Norecords from "../../components/customHelpers/Norecords";
import Notification from "../../components/customHelpers/Notification";
import ConfirmDialog from "../../components/customHelpers/ConfirmDialog";
import ViewNews from "../../components/ViewNews";

const headCells = [
    { id: "title", label: "Title" },
    { id: "status", label: "Status" },
    { id: "createdAt", label: "Posted At" },
    { id: "actions", label: "Actions", disableSorting: true },
];

const makeData = (result) => {
    return result.map((data) => {
        const statusRaw = data.status || (data.approved === true ? "approved" : "pending");
        const status = statusRaw === "pending" ? "Waiting for approval" : statusRaw;
        return {
            title: data.Title || data.title,
            Id: data.Id,
            body: data.Body || data.body,
            author: data.Author || data.author,
            createdAt: data.createdAt,
            status,
        };
    });
};

const CompanyNews = () => {
    const { deleteCompanyNews, getUserNews } = News();
    const [openPopup, setOpenPopup] = useState(false);
    const [openPopupNews, setOpenPopupNews] = useState(false);
    const { NotifyMessage, notify, setNotify } = Notify();
    const [recordForEdit, setRecordForEdit] = useState(null);
    const [newsToView, setNews] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        title: "",
        subTitle: "",
    });

    const [filterFn, setFilterFn] = useState({
        fn: (items) => {
            return items;
        },
    });
    const [values, setValues] = useState({
        loading: false,
        news: [],
        reload: false,
    });
    const { loading, news, reload } = values;

    useEffect(() => {
        fetchCompanyNews();
    }, [reload]);

    const fetchCompanyNews = () => {
        setValues({ ...values, loading: true });
        // NOTE: In the Job Post workflow, companies fetch their APPROVED jobs 
        // but here getUserNews (unapproved) is used to show pending news maybe? 
        // Actually, for CompanyNews view, they usually want to see their own status.
        // Currently getUserNews returns ALL unapproved news.
        // To keep it simple and follow the Job Post pattern, I'll stick to what was there 
        // but ensure data mapping is correct.
        const user = isAuth();
        getUserNews(user?.email).then((data) => {
            if (data.err) {
                NotifyMessage({ message: data.err, type: "error" });
                setValues({ ...values, loading: false });
            } else {
                setValues({
                    ...values,
                    news: makeData(data.result || []),
                    loading: false,
                });
            }
        });
    };

    const handleSearch = (e) => {
        let target = e.target;
        setFilterFn({
            fn: (items) => {
                if (target.value === "") return items;
                return items.filter((x) =>
                    x.title.toLowerCase().includes(target.value.toLowerCase())
                );
            },
        });
    };

    const { TblContainer, TblHead, TblPagination, recordsAfterPagingAndSorting } =
        useTable(
            news.sort((a, b) =>
                a.createdAt < b.createdAt ? 1 : b.createdAt < a.createdAt ? -1 : 0
            ),
            headCells,
            filterFn
        );

    const onDelete = (Id) => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        const user = isAuth();
        deleteCompanyNews(Id, user?.email).then((data) => {
            if (data.err) {
                NotifyMessage({ message: data.err, type: "error" });
            } else {
                NotifyMessage({ message: data.message, type: "success" });
                setValues({ ...values, reload: !reload });
            }
        });
    };

    const user = isAuth();

    return (
        <Layout>
            <Admin>
                <div className="card">
                    <Typography variant="h6" className="page-header">
                        My Company News
                    </Typography>
                    <Toolbar>
                        <Controls.Input
                            label="Search My News"
                            className="searchInput"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AiOutlineSearch />
                                    </InputAdornment>
                                ),
                            }}
                            onChange={handleSearch}
                        />
                        <Controls.Button
                            text="Create News"
                            variant="outlined"
                            startIcon={<IoMdAdd />}
                            className="newButton"
                            onClick={() => {
                                setOpenPopup(true);
                                setRecordForEdit(null);
                            }}
                        />
                    </Toolbar>
                    <Grid container>
                        <Grid item xs={12} className="card__body">
                            {loading ? (
                                <Loading />
                            ) : (
                                <>
                                    <TblContainer>
                                        <TblHead />
                                        <TableBody>
                                            {recordsAfterPagingAndSorting().length > 0 ? (
                                                recordsAfterPagingAndSorting().map((item) => (
                                                    <TableRow key={item.Id}>
                                                        <TableCell>{item.title}</TableCell>
                                                        <TableCell>{item.status}</TableCell>
                                                        <TableCell>
                                                            {new Date(item.createdAt).toLocaleString()}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Controls.ActionButton
                                                                color="primary"
                                                                onClick={() => {
                                                                    setRecordForEdit({ ...item, editing: true });
                                                                    setOpenPopup(true);
                                                                }}
                                                            >
                                                                <FiEdit fontSize="medium" />
                                                            </Controls.ActionButton>
                                                            <Controls.ActionButton
                                                                color="secondary"
                                                                onClick={() => {
                                                                    setConfirmDialog({
                                                                        isOpen: true,
                                                                        title: "Delete news post?",
                                                                        subTitle: "This action cannot be undone.",
                                                                        onConfirm: () => onDelete(item.Id),
                                                                    });
                                                                }}
                                                            >
                                                                <MdOutlineDelete fontSize="medium" />
                                                            </Controls.ActionButton>
                                                            <Controls.ActionButton
                                                                color="primary"
                                                                onClick={() => {
                                                                    setNews(item);
                                                                    setOpenPopupNews(true);
                                                                }}
                                                            >
                                                                <FiEye fontSize="medium" />
                                                            </Controls.ActionButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <Norecords col={4} />
                                            )}
                                        </TableBody>
                                    </TblContainer>
                                    <TblPagination />
                                </>
                            )}
                        </Grid>
                    </Grid>
                    <Popup
                        title="News Form"
                        openPopup={openPopup}
                        setOpenPopup={setOpenPopup}
                    >
                        <NewsForm
                            recordForEdit={recordForEdit}
                            NotifyMessage={NotifyMessage}
                            getAllNews={fetchCompanyNews}
                            setOpenPopup={setOpenPopup}
                            isCompany={true}
                            companyId={user ? user.Id : null}
                        />
                    </Popup>
                    <Popup
                        title="News Preview"
                        openPopup={openPopupNews}
                        setOpenPopup={setOpenPopupNews}
                        scroll="body"
                    >
                        <ViewNews newsToView={newsToView} />
                    </Popup>
                    <Notification notify={notify} setNotify={setNotify} />
                    <ConfirmDialog
                        confirmDialog={confirmDialog}
                        setConfirmDialog={setConfirmDialog}
                    />
                </div>
            </Admin>
        </Layout>
    );
};

export default CompanyNews;
